import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Item, AccountDetail, Card, ItemCardBinding } from '../types';
import {
  getItems,
  getCards,
  getAccountDetails,
  syncItemsFromAccount,
  createItem,
  updateItem,
  deleteItem,
  updateItemMultiSpec,
  updateItemMultiQuantityDelivery,
  updateItemAutoRelist,
  upsertItemCardBinding,
  deleteItemCardBinding
} from '../services/api';
import { Box, RefreshCw, ShoppingBag, Edit, Trash2, Plus, Save, X, Link2, Unlink, CreditCard } from 'lucide-react';
import { showToast } from '../utils/toast';
import Select from './Select';

const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [bindingSaving, setBindingSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBindingModal, setShowBindingModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [bindingItem, setBindingItem] = useState<Item | null>(null);
  const [bindingCardId, setBindingCardId] = useState('');
  const [bindingDescription, setBindingDescription] = useState('');
  const [editForm, setEditForm] = useState<Partial<Item>>({});
  const [addForm, setAddForm] = useState({
    cookie_id: '',
    item_id: '',
    item_title: '',
    item_price: '',
    item_image: '',
    is_multi_spec: false,
    is_multi_qty_ship: false,
    auto_relist_enabled: false
  });

  useEffect(() => {
    getAccountDetails().then(setAccounts);
    getItems().then(setItems);
    getCards().then(setCards);
  }, []);

  useEffect(() => {
    const handleRefreshRequested = () => {
      reloadItems().catch((error) => {
        console.error('刷新商品自动上架状态失败:', error);
      });
    };
    window.addEventListener('items:refresh-requested', handleRefreshRequested);
    return () => window.removeEventListener('items:refresh-requested', handleRefreshRequested);
  }, []);

  const reloadItems = async () => {
    const latestItems = await getItems();
    setItems(latestItems);
    return latestItems;
  };

  const handleSync = async () => {
      if (!selectedAccount) {
        showToast({ type: 'warning', message: '请先选择账号' });
        return;
      }
      setLoading(true);
      try {
        const result = await syncItemsFromAccount(selectedAccount);
        if (result?.success === false) {
          showToast({ type: 'error', message: result.message || '同步商品失败' });
          return;
        }
        await reloadItems();
        showToast({ type: 'success', message: result?.message || '商品同步完成' });
      } catch (error: any) {
        console.error('同步商品失败:', error);
        showToast({ type: 'error', message: error?.message || '同步商品失败，请重试' });
      } finally {
        setLoading(false);
      }
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setEditForm({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    try {
      await updateItem(selectedItem.cookie_id, selectedItem.item_id, {
        item_title: editForm.item_title || '',
        item_description: editForm.item_description || '',
        item_category: editForm.item_category || '',
        item_price: editForm.item_price || '',
        item_image: editForm.item_image || '',
        item_detail: editForm.item_detail || '',
        is_multi_spec: Boolean(editForm.is_multi_spec),
        multi_quantity_delivery: Boolean(editForm.is_multi_qty_ship ?? editForm.multi_quantity_delivery),
        auto_relist_enabled: Boolean(editForm.auto_relist_enabled),
      });
      await reloadItems();
      setShowEditModal(false);
      setSelectedItem(null);
      setEditForm({});
      showToast({ type: 'success', message: '商品信息已更新' });
    } catch (error) {
      console.error('更新商品失败:', error);
      showToast({ type: 'error', message: '更新失败，请重试' });
    }
  };

  const handleDelete = async (item: Item) => {
    if (confirm(`确认删除商品"${item.item_title}"吗？`)) {
      try {
        await deleteItem(item.cookie_id, item.item_id);
        setItems(items.filter(i => !(i.cookie_id === item.cookie_id && i.item_id === item.item_id)));
        showToast({ type: 'success', message: '商品已删除' });
      } catch (error) {
        console.error('删除商品失败:', error);
        showToast({ type: 'error', message: '删除失败，请重试' });
      }
    }
  };

  const openAddModal = () => {
    setAddForm((current) => ({
      ...current,
      cookie_id: current.cookie_id || selectedAccount
    }));
    setShowAddModal(true);
  };

  const handleAddItem = async () => {
    const cookieId = (addForm.cookie_id || selectedAccount).trim();
    const itemId = addForm.item_id.trim();
    const itemTitle = addForm.item_title.trim();

    if (!cookieId) {
      showToast({ type: 'warning', message: '请先选择商品所属账号' });
      return;
    }
    if (!itemId) {
      showToast({ type: 'warning', message: '请输入商品ID' });
      return;
    }
    if (!itemTitle) {
      showToast({ type: 'warning', message: '请输入商品标题' });
      return;
    }

    setAddSaving(true);
    try {
      await createItem(cookieId, {
        item_id: itemId,
        item_title: itemTitle,
        item_price: addForm.item_price.trim(),
        item_image: addForm.item_image.trim(),
        is_multi_spec: addForm.is_multi_spec,
        is_multi_qty_ship: addForm.is_multi_qty_ship,
        auto_relist_enabled: addForm.auto_relist_enabled,
      });
      await reloadItems();
      setSelectedAccount(cookieId);
      setShowAddModal(false);
      setAddForm({
        cookie_id: '',
        item_id: '',
        item_title: '',
        item_price: '',
        item_image: '',
        is_multi_spec: false,
        is_multi_qty_ship: false,
        auto_relist_enabled: false
      });
      showToast({ type: 'success', message: '商品添加成功' });
    } catch (error: any) {
      console.error('添加商品失败:', error);
      showToast({ type: 'error', message: error?.message || '添加失败，请重试' });
    } finally {
      setAddSaving(false);
    }
  };

  const toggleMultiSpec = async (item: Item) => {
    const nextValue = !Boolean(item.is_multi_spec);
    const previousItems = items;
    try {
      setItems(items.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id
          ? { ...i, is_multi_spec: nextValue }
          : i
      ));
      await updateItemMultiSpec(item.cookie_id, item.item_id, nextValue);
      showToast({ type: 'success', message: `多规格已${nextValue ? '开启' : '关闭'}` });
    } catch (error) {
      setItems(previousItems);
      console.error('切换状态失败:', error);
      showToast({ type: 'error', message: '切换多规格状态失败，请重试' });
    }
  };

  const toggleMultiQty = async (item: Item) => {
    const currentValue = Boolean(item.is_multi_qty_ship ?? item.multi_quantity_delivery);
    const nextValue = !currentValue;
    const previousItems = items;
    try {
      setItems(items.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id
          ? { ...i, is_multi_qty_ship: nextValue, multi_quantity_delivery: nextValue }
          : i
      ));
      await updateItemMultiQuantityDelivery(item.cookie_id, item.item_id, nextValue);
      showToast({ type: 'success', message: `多数量发货已${nextValue ? '开启' : '关闭'}` });
    } catch (error) {
      setItems(previousItems);
      console.error('切换状态失败:', error);
      showToast({ type: 'error', message: '切换多数量发货状态失败，请重试' });
    }
  };

  const toggleAutoRelist = async (item: Item) => {
    const nextValue = !Boolean(item.auto_relist_enabled);
    const previousItems = items;
    try {
      setItems(items.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id
          ? { ...i, auto_relist_enabled: nextValue }
          : i
      ));
      await updateItemAutoRelist(item.cookie_id, item.item_id, nextValue);
      showToast({ type: 'success', message: `自动上架已${nextValue ? '开启' : '关闭'}` });
    } catch (error) {
      setItems(previousItems);
      console.error('切换自动上架失败:', error);
      showToast({ type: 'error', message: '切换自动上架失败，请重试' });
    }
  };

  const getCardStockCount = (card: Card) => {
    if (card.stock_count !== undefined && card.stock_count !== null) return card.stock_count;
    if (card.type !== 'data' || !card.data_content) return null;
    return card.data_content.split('\n').filter(line => line.trim()).length;
  };

  const getCardTypeLabel = (type?: Card['type']) => {
    const labels = {
      api: 'API',
      text: '文本',
      data: '批量',
      image: '图片',
    };
    return type ? labels[type] : '卡密';
  };

  const getItemBindings = (item: Item): ItemCardBinding[] => {
    if (item.card_bindings?.length) return item.card_bindings;
    return item.card_binding ? [item.card_binding] : [];
  };

  const openBindingModal = (item: Item) => {
    setBindingItem(item);
    setBindingCardId('');
    setBindingDescription('');
    setShowBindingModal(true);
  };

  const closeBindingModal = () => {
    setShowBindingModal(false);
    setBindingItem(null);
    setBindingCardId('');
    setBindingDescription('');
  };

  const getAvailableCardsForItem = (item: Item | null) => {
    if (!item) return cards;
    const itemIsMultiSpec = Boolean(item.is_multi_spec);
    return cards.filter(card => Boolean(card.is_multi_spec) === itemIsMultiSpec);
  };

  const handleSaveBinding = async () => {
    if (!bindingItem) return;
    if (!bindingCardId) {
      showToast({ type: 'warning', message: '请选择要关联的卡密' });
      return;
    }

    const selectedCard = cards.find(card => String(card.id) === bindingCardId);
    if (!selectedCard) {
      showToast({ type: 'warning', message: '请选择有效的卡密' });
      return;
    }

    if (Boolean(bindingItem.is_multi_spec) !== Boolean(selectedCard.is_multi_spec)) {
      showToast({
        type: 'warning',
        message: Boolean(bindingItem.is_multi_spec)
          ? '多规格商品必须选择多规格卡密'
          : '普通商品请绑定普通卡密'
      });
      return;
    }

    setBindingSaving(true);
    try {
      await upsertItemCardBinding({
        cookie_id: bindingItem.cookie_id,
        item_id: bindingItem.item_id,
        card_id: Number(bindingCardId),
        enabled: true,
        description: bindingDescription.trim()
      });
      const latestItems = await reloadItems();
      const latestItem = latestItems.find(item => item.cookie_id === bindingItem.cookie_id && item.item_id === bindingItem.item_id);
      if (latestItem) setBindingItem(latestItem);
      setBindingCardId('');
      setBindingDescription('');
      showToast({ type: 'success', message: '卡密关联成功' });
    } catch (error: any) {
      console.error('关联卡密失败:', error);
      showToast({ type: 'error', message: error?.message || '关联失败，请重试' });
    } finally {
      setBindingSaving(false);
    }
  };

  const handleDeleteBinding = async (binding: ItemCardBinding) => {
    if (!confirm(`确认解除与“${binding.card_name || `卡密 ${binding.card_id}`}”的关联吗？`)) return;
    try {
      await deleteItemCardBinding(binding.id);
      const latestItems = await reloadItems();
      if (bindingItem) {
        const latestItem = latestItems.find(item => item.cookie_id === bindingItem.cookie_id && item.item_id === bindingItem.item_id);
        if (latestItem) setBindingItem(latestItem);
      }
      showToast({ type: 'success', message: '已解除卡密关联' });
    } catch (error: any) {
      console.error('解除卡密关联失败:', error);
      showToast({ type: 'error', message: error?.message || '解除失败，请重试' });
    }
  };

  const accountOptions = accounts.map(acc => ({
    value: acc.id,
    label: acc.nickname || acc.name || acc.remark || acc.username || acc.id
  }));

  const formatItemPrice = (price?: string | number | null) => {
    const value = String(price ?? '').trim();
    if (!value) return '';
    return `\u00A5${value.replace(/^[\u00A5\uFFE5\s]+/, '')}`;
  };

  const getRelistStatusView = (item: Item) => {
    const status = String(item.last_relist_status || '').trim();
    if (!status) return null;

    const views: Record<string, { label: string; className: string }> = {
      pending: { label: '上架中', className: 'bg-amber-50 text-amber-700' },
      submitted: { label: '已提交上架', className: 'bg-blue-50 text-blue-700' },
      success: { label: '上次上架成功', className: 'bg-green-50 text-green-700' },
      failed: { label: '上架失败', className: 'bg-red-50 text-red-700' },
      skipped: { label: '已跳过上架', className: 'bg-gray-100 text-gray-500' },
    };

    return views[status] || { label: status, className: 'bg-gray-100 text-gray-600' };
  };

  const bindingAvailableCards = getAvailableCardsForItem(bindingItem);
  const bindingExistingBindings = bindingItem ? getItemBindings(bindingItem) : [];
  const bindingCardOptions = bindingAvailableCards.map(card => {
    const stockCount = getCardStockCount(card);
    const specText = card.is_multi_spec && card.spec_name && card.spec_value
      ? `${card.spec_name}:${card.spec_value}`
      : '';
    return {
      value: String(card.id),
      label: card.name || `卡密 ${card.id}`,
      description: [
        getCardTypeLabel(card.type),
        specText,
        stockCount !== null ? `库存 ${stockCount}` : ''
      ].filter(Boolean).join(' · ')
    };
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <h2 className="text-3xl font-bold text-gray-900">商品管理</h2>
          <p className="text-gray-500 mt-2 text-sm">监控并管理所有账号下的闲鱼商品。</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-3 xl:w-auto xl:justify-end">
            <Select
                className="w-full flex-none sm:w-64 xl:w-60 2xl:w-72"
                value={selectedAccount}
                onChange={setSelectedAccount}
                options={[{ value: '', label: '选择账号以同步' }, ...accountOptions]}
                placeholder="选择账号以同步"
            />
            <button
                onClick={handleSync}
                disabled={loading || !selectedAccount}
                className="ios-btn-primary flex h-12 min-w-[132px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-2xl px-5 py-3 font-bold shadow-lg shadow-yellow-200 disabled:opacity-50 sm:flex-none"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                同步商品
            </button>
            <button
              onClick={openAddModal}
              className="flex h-12 min-w-[132px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-gray-900 px-5 py-3 font-bold text-white shadow-lg transition-colors hover:bg-gray-800 sm:flex-none"
            >
              <Plus className="w-4 h-4" />
              添加商品
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map(item => {
            const bindings = getItemBindings(item);
            const primaryBinding = bindings[0];
            const relistStatusView = getRelistStatusView(item);
            return (
              <div key={`${item.cookie_id}-${item.item_id}`} className="ios-card p-4 rounded-3xl hover:shadow-lg transition-all group relative">
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-[#FFE815] transition-colors"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-red-100 text-red-500 transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                  </div>
                  <div className="aspect-square bg-gray-100 rounded-2xl mb-4 overflow-hidden relative">
                      {item.item_image ? (
                          <img src={item.item_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <Box className="w-10 h-10" />
                          </div>
                      )}
                      <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded-lg">
                          {formatItemPrice(item.item_price)}
                      </div>
                  </div>
                  <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-2 h-10">{item.item_title}</h3>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                      <span className="bg-gray-100 px-2 py-1 rounded-md truncate max-w-[100px]">ID: {item.item_id}</span>
                  </div>
                  <div className="mb-2 rounded-xl border border-gray-100 bg-gray-50 p-2.5">
                    {primaryBinding ? (
                      <div className="space-y-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <Link2 className="h-4 w-4 flex-none text-green-600" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-extrabold text-gray-900">
                              {primaryBinding.card_name || `卡密 ${primaryBinding.card_id}`}
                            </div>
                            <div className="truncate text-[11px] font-medium text-gray-500">
                              {bindings.length > 1 ? `已关联 ${bindings.length} 个规格` : '已关联卡密'}
                              {primaryBinding.spec_name && primaryBinding.spec_value ? ` · ${primaryBinding.spec_name}:${primaryBinding.spec_value}` : ''}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => openBindingModal(item)}
                          className="w-full rounded-lg bg-white px-2 py-1.5 text-xs font-bold text-gray-700 shadow-sm transition-colors hover:bg-[#FFE815]"
                        >
                          管理卡密
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openBindingModal(item)}
                        className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-white px-2 py-2 text-xs font-bold text-gray-700 shadow-sm transition-colors hover:bg-[#FFE815]"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        关联卡密
                      </button>
                    )}
                  </div>
                  {relistStatusView && (
                    <div
                      className={`mb-2 truncate rounded-lg px-2.5 py-1.5 text-[11px] font-bold ${relistStatusView.className}`}
                      title={item.last_relist_error || item.last_relist_at || relistStatusView.label}
                    >
                      {relistStatusView.label}
                      {item.last_relist_at ? ` · ${item.last_relist_at}` : ''}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => toggleMultiSpec(item)}
                        className={`text-[11px] font-bold px-1.5 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_spec
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多规格
                      </button>
                      <button
                        onClick={() => toggleMultiQty(item)}
                        className={`text-[11px] font-bold px-1.5 py-1.5 rounded-lg transition-colors ${
                          Boolean(item.is_multi_qty_ship ?? item.multi_quantity_delivery)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多数量发货
                      </button>
                      <button
                        onClick={() => toggleAutoRelist(item)}
                        title="买家付款后自动尝试重新上架该商品"
                        className={`text-[11px] font-bold px-1.5 py-1.5 rounded-lg transition-colors ${
                          Boolean(item.auto_relist_enabled)
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        自动上架
                      </button>
                  </div>
              </div>
            );
          })}
          {items.length === 0 && (
             <div className="col-span-full py-20 text-center text-gray-400">
                 <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                 暂无商品数据，请选择账号进行同步
             </div>
          )}
      </div>

      {showEditModal && selectedItem && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container">
            <div className="modal-header">
              <div className="flex items-center justify-between w-full">
                <div>
                  <h3 className="text-2xl font-extrabold text-gray-900">编辑商品</h3>
                  <p className="text-sm text-gray-500 mt-1">ID: {selectedItem.item_id}</p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="modal-body space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">商品标题</label>
                <input
                  value={editForm.item_title || ''}
                  onChange={(e) => setEditForm({ ...editForm, item_title: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">价格</label>
                  <input
                    value={editForm.item_price || ''}
                    onChange={(e) => setEditForm({ ...editForm, item_price: e.target.value })}
                    className="w-full ios-input px-4 py-3 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">分类</label>
                  <input
                    value={editForm.item_category || ''}
                    onChange={(e) => setEditForm({ ...editForm, item_category: e.target.value })}
                    className="w-full ios-input px-4 py-3 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">预览图 URL</label>
                <input
                  value={editForm.item_image || ''}
                  onChange={(e) => setEditForm({ ...editForm, item_image: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                  placeholder="https://..."
                />
                {editForm.item_image && (
                  <div className="mt-3 w-24 h-24 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                    <img src={editForm.item_image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">商品详情</label>
                <textarea
                  value={editForm.item_detail || ''}
                  onChange={(e) => setEditForm({ ...editForm, item_detail: e.target.value })}
                  rows={4}
                  className="w-full ios-input px-4 py-3 rounded-xl resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <span className="text-sm font-bold text-gray-700">多规格</span>
                  <input
                    type="checkbox"
                    checked={Boolean(editForm.is_multi_spec)}
                    onChange={(e) => setEditForm({ ...editForm, is_multi_spec: e.target.checked })}
                    className="w-5 h-5"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <span className="text-sm font-bold text-gray-700">多数量发货</span>
                  <input
                    type="checkbox"
                    checked={Boolean(editForm.is_multi_qty_ship ?? editForm.multi_quantity_delivery)}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      is_multi_qty_ship: e.target.checked,
                      multi_quantity_delivery: e.target.checked
                    })}
                    className="w-5 h-5"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <span className="text-sm font-bold text-gray-700">自动上架</span>
                  <input
                    type="checkbox"
                    checked={Boolean(editForm.auto_relist_enabled)}
                    onChange={(e) => setEditForm({ ...editForm, auto_relist_enabled: e.target.checked })}
                    className="w-5 h-5"
                  />
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  保存更改
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showAddModal && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container">
            <div className="modal-header">
              <div className="flex items-center justify-between w-full">
                <div>
                  <h3 className="text-2xl font-extrabold text-gray-900">添加商品</h3>
                  <p className="text-sm text-gray-500 mt-1">手动录入商品基础信息，用于规则匹配和管理。</p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                  type="button"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="modal-body space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">所属账号</label>
                <Select
                  value={addForm.cookie_id || selectedAccount}
                  onChange={(value) => setAddForm({ ...addForm, cookie_id: value })}
                  className="w-full"
                  options={[{ value: '', label: '请选择账号' }, ...accountOptions]}
                  placeholder="请选择账号"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">商品ID</label>
                  <input
                    value={addForm.item_id}
                    onChange={(e) => setAddForm({ ...addForm, item_id: e.target.value })}
                    className="w-full ios-input px-4 py-3 rounded-xl"
                    placeholder="请输入闲鱼商品ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">价格</label>
                  <input
                    value={addForm.item_price}
                    onChange={(e) => setAddForm({ ...addForm, item_price: e.target.value })}
                    className="w-full ios-input px-4 py-3 rounded-xl"
                    placeholder="例如 99.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">商品标题</label>
                <input
                  value={addForm.item_title}
                  onChange={(e) => setAddForm({ ...addForm, item_title: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                  placeholder="请输入商品标题"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">预览图 URL</label>
                <input
                  value={addForm.item_image}
                  onChange={(e) => setAddForm({ ...addForm, item_image: e.target.value })}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                  placeholder="https://..."
                />
                {addForm.item_image && (
                  <div className="mt-3 w-24 h-24 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                    <img src={addForm.item_image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <span className="text-sm font-bold text-gray-700">多规格</span>
                  <input
                    type="checkbox"
                    checked={addForm.is_multi_spec}
                    onChange={(e) => setAddForm({ ...addForm, is_multi_spec: e.target.checked })}
                    className="w-5 h-5"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <span className="text-sm font-bold text-gray-700">多数量发货</span>
                  <input
                    type="checkbox"
                    checked={addForm.is_multi_qty_ship}
                    onChange={(e) => setAddForm({ ...addForm, is_multi_qty_ship: e.target.checked })}
                    className="w-5 h-5"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <span className="text-sm font-bold text-gray-700">自动上架</span>
                  <input
                    type="checkbox"
                    checked={addForm.auto_relist_enabled}
                    onChange={(e) => setAddForm({ ...addForm, auto_relist_enabled: e.target.checked })}
                    className="w-5 h-5"
                  />
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  type="button"
                >
                  取消
                </button>
                <button
                  onClick={handleAddItem}
                  disabled={addSaving}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  type="button"
                >
                  <Plus className="w-4 h-4" />
                  {addSaving ? '添加中...' : '添加商品'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showBindingModal && bindingItem && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container">
            <div className="modal-header">
              <div className="flex items-center justify-between w-full">
                <div className="min-w-0">
                  <h3 className="text-2xl font-extrabold text-gray-900">关联卡密</h3>
                  <p className="mt-1 truncate text-sm text-gray-500">{bindingItem.item_title || bindingItem.item_id}</p>
                </div>
                <button
                  onClick={closeBindingModal}
                  className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                  type="button"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="modal-body space-y-5">
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-extrabold text-gray-900">
                  <CreditCard className="h-4 w-4" />
                  已关联卡密
                </div>
                {bindingExistingBindings.length > 0 ? (
                  <div className="space-y-2">
                    {bindingExistingBindings.map(binding => (
                      <div key={binding.id} className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-gray-900">
                            {binding.card_name || `卡密 ${binding.card_id}`}
                          </div>
                          <div className="mt-0.5 truncate text-xs font-medium text-gray-500">
                            {getCardTypeLabel(binding.card_type)}
                            {binding.spec_name && binding.spec_value ? ` · ${binding.spec_name}:${binding.spec_value}` : ''}
                            {binding.stock_count !== null && binding.stock_count !== undefined ? ` · 库存 ${binding.stock_count}` : ''}
                            {binding.enabled ? '' : ' · 已停用'}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteBinding(binding)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors hover:bg-red-100"
                          title="解除关联"
                        >
                          <Unlink className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl bg-white p-4 text-sm font-medium text-gray-500">
                    当前商品还没有关联卡密。
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {Boolean(bindingItem.is_multi_spec) ? '选择多规格卡密' : '选择普通卡密'}
                </label>
                <Select
                  value={bindingCardId}
                  onChange={setBindingCardId}
                  className="w-full"
                  options={[{ value: '', label: '请选择卡密' }, ...bindingCardOptions]}
                  placeholder="请选择卡密"
                  emptyLabel={Boolean(bindingItem.is_multi_spec) ? '暂无可用的多规格卡密' : '暂无可用的普通卡密'}
                />
                <p className="mt-2 text-xs font-medium text-gray-500">
                  {Boolean(bindingItem.is_multi_spec)
                    ? '多规格商品会按卡密规格与订单规格精确匹配。'
                    : '普通商品付款后会发送这里关联的卡密。'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">备注（可选）</label>
                <input
                  value={bindingDescription}
                  onChange={(e) => setBindingDescription(e.target.value)}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                  placeholder="例如：主套餐、备用批次"
                />
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={closeBindingModal}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  type="button"
                >
                  取消
                </button>
                <button
                  onClick={handleSaveBinding}
                  disabled={bindingSaving || !bindingCardId}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  type="button"
                >
                  <Link2 className="w-4 h-4" />
                  {bindingSaving ? '保存中...' : '保存关联'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ItemList;
